import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, Truck, Shield, RotateCcw, ChevronRight, Minus, Plus, Heart, Share2, ChevronLeft } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { useStore } from '../admin/context/ProductStore';
import './ProductDetail.css';

export default function ProductDetail({ onAddToCart }) {
  const { products = [], getFullUrl, loading } = useStore();
  const { slug } = useParams();
  const product = products.find(p => p.slug === slug);
  const [qty, setQty] = useState(1);
  const [selectedWeight, setSelectedWeight] = useState('');
  const [wished, setWished] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  const [activeImg, setActiveImg] = useState(0);
  const [isAdded, setIsAdded] = useState(false);

  // Set initial weight when product is loaded
  useEffect(() => {
    if (product?.variants?.length > 0) {
      setSelectedWeight(product.variants[0].weight);
    } else if (product?.weight) {
      const weights = product.weight.split(',').map(w => w.trim()).filter(Boolean);
      if (weights.length > 0) setSelectedWeight(weights[0]);
    }
  }, [product]);

  if (loading) {
    return (
      <div className="container" style={{ padding: '100px 24px', textAlign: 'center' }}>
        <div className="loading-spinner"></div>
        <p style={{ marginTop: '20px', color: 'var(--text-muted)' }}>Loading product details...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <main className="container" style={{ padding: '100px 24px', textAlign: 'center' }}>
        <h2 style={{ fontSize: '32px', marginBottom: '16px' }}>Product not found</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: '32px' }}>The product you are looking for doesn't exist or has been moved.</p>
        <Link to="/shop" className="btn-primary" style={{ display: 'inline-flex' }}>
          Back to Shop
        </Link>
      </main>
    );
  }

  const weights = product.variants?.length > 0 
    ? product.variants.map(v => v.weight) 
    : (product.weight ? product.weight.split(',').map(w => w.trim()).filter(Boolean) : []);
  
  const selectedVariant = product.variants?.find(v => v.weight === selectedWeight);
  const displayPrice = selectedVariant?.price || product.price;
  const displayMrp = selectedVariant?.mrp || product.mrp;
  const highlights = Array.isArray(product.highlights) ? product.highlights : [];
  const images = product.images?.length > 0 
    ? product.images.map(i => getFullUrl(i.url)) 
    : [getFullUrl(product.image)];

  const discount = displayMrp > 0 ? Math.round(((displayMrp - displayPrice) / displayMrp) * 100) : 0;
  const related = products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);



  const handleAddToCart = () => {
    if (onAddToCart) {
      const cartItem = { 
        ...product, 
        weight: selectedWeight || product.weight,
        price: displayPrice,
        mrp: displayMrp
      };
      for (let i = 0; i < qty; i++) onAddToCart(cartItem);
      setIsAdded(true);
      setTimeout(() => setIsAdded(false), 2000);
    }
  };

  return (
    <main className="product-detail-page">
      {/* Breadcrumb */}
      <div className="breadcrumb-bar container">
        <Link to="/">Home</Link>
        <ChevronRight size={14} />
        <Link to="/shop">Shop</Link>
        <ChevronRight size={14} />
        <span>{product.name}</span>
      </div>

      <div className="container product-detail-layout">
        {/* Image */}
        <div className="detail-img-section">
          <div className="detail-img-main">
            <img
              src={images[activeImg]}
              alt={product.name}
              onError={(e) => {
                e.target.src = `https://via.placeholder.com/600x600/fdf8f0/7a4e2d?text=${encodeURIComponent(product.name)}`;
              }}
            />
            {product.badge && (
              <span className={`badge ${product.badgeType === 'gold' ? 'badge-gold' : 'badge-green'} detail-badge`}>
                {product.badge}
              </span>
            )}
            
            {images.length > 1 && (
              <>
                <button 
                  className="slider-nav-btn prev" 
                  onClick={() => setActiveImg((prev) => (prev === 0 ? images.length - 1 : prev - 1))}
                >
                  <ChevronLeft size={24} />
                </button>
                <button 
                  className="slider-nav-btn next" 
                  onClick={() => setActiveImg((prev) => (prev === images.length - 1 ? 0 : prev + 1))}
                >
                  <ChevronRight size={24} />
                </button>
                <div className="slider-dots">
                  {images.map((_, i) => (
                    <span 
                      key={i} 
                      className={`dot ${activeImg === i ? 'active' : ''}`}
                      onClick={() => setActiveImg(i)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
          
          {images.length > 1 && (
            <div className="detail-img-thumbs">
              {images.map((url, i) => (
                <div 
                  key={i} 
                  className={`thumb-item ${activeImg === i ? 'active' : ''}`}
                  onClick={() => setActiveImg(i)}
                >
                  <img src={url} alt={`${product.name} ${i+1}`} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="detail-info-section">


          <h1 className="detail-name">{product.name}</h1>
          {product.shortDesc && <p className="detail-short-desc">{product.shortDesc}</p>}

          <div className="detail-price-row">
            <span className="detail-price">₹{displayPrice}</span>
            <span className="detail-mrp">₹{displayMrp}</span>
            <span className="detail-discount">{discount}% off</span>
          </div>

          <p className="detail-desc">{product.description}</p>

          {/* Highlights */}
          {highlights.length > 0 && (
            <div className="detail-highlights">
              {highlights.map(h => (
                <span key={h} className="highlight-chip">✓ {h}</span>
              ))}
            </div>
          )}

          {/* Weight options */}
          {weights.length > 0 && (
            <div className="detail-section">
              <p className="detail-label">{weights.length > 1 ? 'Select Weight' : 'Weight'}</p>
              <div className="weight-options">
                {weights.map(w => (
                  <button
                    key={w}
                    className={`weight-btn ${selectedWeight === w ? 'active' : ''}`}
                    onClick={() => weights.length > 1 && setSelectedWeight(w)}
                    style={{ cursor: weights.length > 1 ? 'pointer' : 'default' }}
                  >
                    {w}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Qty + Add */}
          <div className="detail-section detail-actions">
            <div className="qty-control">
              <button className="qty-btn" onClick={() => setQty(Math.max(1, qty - 1))}><Minus size={16} /></button>
              <span className="qty-val">{qty}</span>
              <button className="qty-btn" onClick={() => setQty(qty + 1)}><Plus size={16} /></button>
            </div>
            <button 
              className={`btn-primary add-btn ${isAdded ? 'added' : ''}`} 
              onClick={handleAddToCart}
              disabled={(selectedVariant ? selectedVariant.stock === 'outofstock' : product.stock === 'outofstock')}
              style={{
                backgroundColor: (selectedVariant ? selectedVariant.stock === 'outofstock' : product.stock === 'outofstock') ? '#ccc' : undefined,
                cursor: (selectedVariant ? selectedVariant.stock === 'outofstock' : product.stock === 'outofstock') ? 'not-allowed' : 'pointer'
              }}
            >
              {isAdded 
                ? '✓ Added to Cart' 
                : (selectedVariant ? selectedVariant.stock === 'outofstock' : product.stock === 'outofstock')
                  ? 'Out of Stock'
                  : `Add to Cart — ₹${displayPrice * qty}`
              }
            </button>
            <button
              className={`wish-btn ${wished ? 'wished' : ''}`}
              onClick={() => setWished(!wished)}
              aria-label="Wishlist"
            >
              <Heart size={20} fill={wished ? 'currentColor' : 'none'} />
            </button>
            <button className="wish-btn" aria-label="Share">
              <Share2 size={18} />
            </button>
          </div>

          {/* Trust */}
          <div className="detail-trust">
            <div className="trust-row">
              <Truck size={16} className="trust-ico" />
              <span>Free delivery on orders ₹599+</span>
            </div>
            <div className="trust-row">
              <Shield size={16} className="trust-ico" />
              <span>100% authentic & quality guaranteed</span>
            </div>
            <div className="trust-row">
              <RotateCcw size={16} className="trust-ico" />
              <span>Easy 7-day returns</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="detail-tabs-section">
        <div className="container">
          <div className="detail-tabs">
            {['description', 'nutrition'].map(tab => (
              <button
                key={tab}
                className={`detail-tab ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          <div className="detail-tab-content">
            {activeTab === 'description' && (
              <div className="tab-body">
                <p>{product.description || 'No description available.'}</p>
                {highlights.length > 0 && (
                  <ul>
                    {highlights.map(h => <li key={h}>{h}</li>)}
                  </ul>
                )}
                <p>Store in a cool, dry place away from direct sunlight. Best consumed within 6 months of opening.</p>
              </div>
            )}
            {activeTab === 'nutrition' && (
              <div className="tab-body">
                <table className="nutrition-table">
                  <thead><tr><th>Nutrient</th><th>Per 100g</th><th>% Daily Value</th></tr></thead>
                  <tbody>
                    {[['Calories','579 kcal','29%'],['Protein','21g','42%'],['Total Fat','50g','64%'],['Carbohydrates','22g','8%'],['Fibre','12g','43%'],['Vitamin E','25mg','167%'],['Magnesium','270mg','64%']].map(([n,v,d]) => (
                      <tr key={n}><td>{n}</td><td>{v}</td><td>{d}</td></tr>
                    ))}
                  </tbody>
                </table>
                <p className="nutrition-note">*Approximate values. Actual values may vary.</p>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <section className="section container">
          <div className="section-header">
            <span className="eyebrow">More to Explore</span>
            <h2 className="section-title">Related Products</h2>
          </div>
          <div className="grid-4">
            {related.map(p => <ProductCard key={p.id} product={p} onAddToCart={onAddToCart} />)}
          </div>
        </section>
      )}
    </main>
  );
}
